import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, GitBranch, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import type { Book, EditionTreeResponse } from '../../../shared/types/index.js';
import { api } from '../../../shared/api/api.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { canCreateNewEdition } from '../../books/components/book-list/bookListUtils.js';
import { EditionTreeVisualizer } from './EditionTreeVisualizer.js';
import { CreateEditionModal } from './CreateEditionModal.js';
import { useBodyScrollLock } from '../../../shared/utils/scrollLock.js';

interface EditionTreeModalProps {
  book: Book | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectBook?: (book: Book) => void;
  onBookCreated?: (newBook: Book) => void;
}

export const EditionTreeModal: React.FC<EditionTreeModalProps> = ({
  book,
  isOpen,
  onClose,
  onSelectBook,
  onBookCreated,
}) => {
  const { user } = useAuthStore();
  const [treeData, setTreeData] = useState<EditionTreeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sub-modal for creating a new edition
  const [createEditionParent, setCreateEditionParent] = useState<Book | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const fetchTree = async (targetId: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getEditionTree(targetId);
      setTreeData(data);
    } catch (err: any) {
      setError(err.message || 'Gagal memuat pohon silsilah edisi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && book) {
      fetchTree(book.id);
    } else {
      setTreeData(null);
      setError(null);
    }
  }, [isOpen, book?.id]);

  useBodyScrollLock(isOpen);

  if (!isOpen || !book) return null;

  const handleCreateEditionRequested = (parent: Book) => {
    setCreateEditionParent(parent);
    setIsCreateModalOpen(true);
  };

  const handleEditionCreated = (newBook: Book) => {
    if (onBookCreated) {
      onBookCreated(newBook);
    }
    // Refresh the edition tree
    fetchTree(book.id);
  };

  const modalContent = (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/75 backdrop-blur-xs p-0 sm:p-4 md:p-6 overflow-hidden animate-in fade-in duration-200"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="bg-white sm:rounded-2xl w-full max-w-5xl h-full sm:h-[92vh] max-h-[100dvh] sm:max-h-[92vh] overflow-hidden shadow-2xl border-0 sm:border sm:border-slate-200 flex flex-col">
          {/* Top Modal Navigation */}
          <div className="px-3 sm:px-5 py-2.5 sm:py-3.5 border-b border-slate-200 flex items-center justify-between bg-white shrink-0">
            <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
              <span className="p-1.5 sm:p-2 bg-indigo-50 text-indigo-700 rounded-xl shrink-0">
                <GitBranch className="w-4 h-4 sm:w-5 sm:h-5" />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                  <h2 className="text-sm sm:text-base font-bold text-slate-900 truncate">
                    Hubungan Antar-Edisi & Silsilah
                  </h2>
                  {treeData && (
                    <span className="text-[10px] sm:text-xs bg-indigo-100 text-indigo-800 font-semibold px-2 py-0.5 rounded-full shrink-0">
                      {treeData.totalEditions} Edisi
                    </span>
                  )}
                </div>
                <p className="text-[11px] sm:text-xs text-slate-500 truncate hidden xs:block">
                  Visualisasi relasi naskah orisinal, revisi berkala, cetakan baru, dan format digital
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => fetchTree(book.id)}
                title="Muat ulang silsilah"
                className="p-1.5 text-slate-600 hover:text-slate-900 rounded-lg hover:bg-slate-200 transition cursor-pointer"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                id="btn-close-edition-tree-modal"
                type="button"
                onClick={onClose}
                aria-label="Tutup silsilah"
                title="Tutup (Esc)"
                className="w-8 h-8 rounded-lg bg-slate-200/70 hover:bg-rose-100 text-slate-700 hover:text-rose-700 border border-slate-300 hover:border-rose-300 flex items-center justify-center transition cursor-pointer shrink-0 shadow-2xs"
              >
                <X className="w-4.5 h-4.5 stroke-[2.2]" />
              </button>
            </div>
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-hidden p-0 sm:p-4 bg-slate-100/50 flex flex-col min-h-0">
            {loading ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 text-slate-500 p-6">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                <span className="text-xs sm:text-sm font-medium">Memuat struktur silsilah edisi buku...</span>
              </div>
            ) : error ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
                <AlertCircle className="w-10 h-10 text-rose-500" />
                <div className="max-w-md">
                  <h4 className="text-sm font-bold text-slate-900 mb-1">Gagal Memuat Pohon Edisi</h4>
                  <p className="text-xs text-slate-500 mb-4">{error}</p>
                  <button
                    type="button"
                    onClick={() => fetchTree(book.id)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-semibold hover:bg-indigo-700 transition"
                  >
                    Coba Lagi
                  </button>
                </div>
              </div>
            ) : treeData ? (
              <div className="flex-1 h-full overflow-hidden flex flex-col min-h-0">
                <EditionTreeVisualizer
                  treeData={treeData}
                  currentBookId={book.id}
                  onSelectBook={(selected) => {
                    if (onSelectBook) {
                      onSelectBook(selected);
                    }
                  }}
                  onRequestCreateEdition={
                    canCreateNewEdition(book, user) ? handleCreateEditionRequested : undefined
                  }
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Submodal for creating new edition */}
      <CreateEditionModal
        parentBook={createEditionParent}
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setCreateEditionParent(null);
        }}
        onEditionCreated={handleEditionCreated}
      />
    </>
  );

  return createPortal(modalContent, document.body);
};
